import React, { useMemo } from 'react';
import { Card, Col, Empty, Radio, Row, Space } from 'antd';
import { MIDDLE_STYLE } from '@constants/responsive';
import { iterateObject } from '@utils/array.util';
import { UIReservationStatus } from '@core/models';
import { useBookingState } from '@core/configureStore';
import {
  CardSectionHeader,
  LoadableContainer,
  ReservationGroupedContainer,
  ReservationListColumnHeader,
} from '@components/index';
import SplashScreen from '@components/SplashScreen';
import { ReservationGroupBy, useBookingAppTourStore } from '@core/hooks/useBookingAppTourStore';
import { renderReservationStatusStr } from '@utils/reservation.util';
import { useExtendedBooking, useOptimisticBookingState, useRealTime } from '@core/hooks';
import { GLOBAL_THEME_COLOR } from '@constants/theme';
import { useReservationTimelineParams } from '@core/hooks/useReservationParams';

const StatisticCard = ({ title, value }: { title: string; value: any }) => {
  return (
    <Card className="no-padding" style={{ width: 'fit-content', borderRadius: 10 }}>
      <div
        style={{
          padding: '15px 15px',
        }}>
        <CardSectionHeader title={title} />
        <h3
          style={{
            fontSize: '18px',
            color: GLOBAL_THEME_COLOR.$highlight_color,
            textAlign: 'right',
          }}>
          {value}
        </h3>
      </div>
    </Card>
  );
};

const CapacityStatisticCard = () => {
  const { now } = useRealTime();
  const { getHourCapacityPercentage } = useReservationTimelineParams();

  const currentCapacity = useMemo(() => {
    return getHourCapacityPercentage(now.hours());
  }, [now, getHourCapacityPercentage]);

  return <StatisticCard title="Restaurant Capacity" value={`${currentCapacity}%`} />;
};

const TimeCard = () => {
  const { now } = useRealTime();
  return <StatisticCard title="Current Time" value={`${now.format('HH:mm:ss')}`} />;
};

const BookingReservationTab: React.FC<any> = () => {
  const { isLoading } = useBookingState();
  const { reservations: restaurantReservations } = useOptimisticBookingState();
  const { groupedReservations, sortedAndFilteredReservations } = useExtendedBooking();
  const { selectedStatus, selectedGroupdBy, setSelectedStatus, setSelectedGroupBy } =
    useBookingAppTourStore();

  const totalGuests = useMemo(() => {
    let value = 0;
    for (const reservation of restaurantReservations) {
      value += reservation.numberOfPeople;
    }
    return value;
  }, [restaurantReservations]);

  return (
    <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <Row gutter={15} style={{ marginBottom: 20, marginTop: 10 }}>
        <Col>
          <TimeCard />
        </Col>
        <Col>
          <StatisticCard title="Total Reservations" value={restaurantReservations.length} />
        </Col>
        <Col>
          <StatisticCard title="Total Guests" value={totalGuests} />
        </Col>
        <Col>
          <CapacityStatisticCard />
        </Col>
      </Row>
      <Space size={40} style={{ flexWrap: 'wrap' }}>
        <Space size={20} style={{ ...MIDDLE_STYLE, width: 'fit-content' }}>
          <CardSectionHeader title="Group By" />
          <Radio.Group onChange={e => setSelectedGroupBy(e.target.value)} value={selectedGroupdBy}>
            {iterateObject(ReservationGroupBy, key => (
              <Radio.Button value={key}>{(ReservationGroupBy as any)[key]}</Radio.Button>
            ))}
          </Radio.Group>
        </Space>
        <Space size={20} style={{ ...MIDDLE_STYLE, width: 'fit-content' }}>
          <CardSectionHeader title="Status" />
          <Radio.Group onChange={e => setSelectedStatus(e.target.value)} value={selectedStatus}>
            {iterateObject(UIReservationStatus, key => {
              const reservationStatus = (UIReservationStatus as any)[key];
              const reservationStatusStr = renderReservationStatusStr(reservationStatus);
              return (
                reservationStatusStr && (
                  <Radio.Button value={reservationStatus}>{reservationStatusStr}</Radio.Button>
                )
              );
            })}
          </Radio.Group>
        </Space>
      </Space>
      <LoadableContainer isLoading={isLoading} loadComponent={<SplashScreen />}>
        <LoadableContainer
          isLoading={sortedAndFilteredReservations.length === 0}
          loadComponent={
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>No reservation found</span>}
            />
          }>
          <div style={{ width: '100%', position: 'relative' }}>
            <Space size={10} direction={'vertical'} style={{ width: '100%' }}>
              {iterateObject(groupedReservations, (key, _reservations) => (
                <ReservationGroupedContainer
                  key={key}
                  title={key}
                  reservations={_reservations}
                  renderExtraParamsHeader={() => (
                    <React.Fragment>
                      <ReservationListColumnHeader title="Contact" />
                    </React.Fragment>
                  )}
                  renderExtraParams={reservation => (
                    <React.Fragment>{reservation.email || reservation.phone}</React.Fragment>
                  )}
                />
              ))}
            </Space>
          </div>
        </LoadableContainer>
      </LoadableContainer>
    </div>
  );
};

export default BookingReservationTab;
